import { Injectable } from '@angular/core';
import {
	ICmmsFilter,
	ICmmsSelectedFilter,
} from '@app/pages/cmms/components/cmms-sort-filters-grouping/interfaces-and-types/filter.interfaces';
import { BehaviorSubject, Observable, Subject, map } from 'rxjs';
import { iconEnum } from '@app/configs/model/IconEnum';
import { FilterTypes } from '../interfaces-and-types/filter.types';

@Injectable()
export class CmmsFiltersService {
	filterTypeIcons: Record<FilterTypes, iconEnum> = {
		text: iconEnum.text,
		multitext: iconEnum.multitext,
		status: iconEnum.speedometer,
		multiselect: iconEnum.multiselect,
		classification: iconEnum.multiselect,
		location: iconEnum.tree,
		binary: iconEnum.routine,
		date: iconEnum.calendar,
		assignee: iconEnum.user,
		countRange: iconEnum.count_range,
		priority: iconEnum.priority,
		documentType: iconEnum.multiselect,
		bim: iconEnum.ifc,
		dateInterval: iconEnum.calendar,
	};

	selectedFilters$: Observable<ICmmsSelectedFilter[]>;
	filterList$: Observable<ICmmsFilter[]>;

	defaultFilterValues = new Map<string, any>();
	selectedFiltersVariablesMap = new Map<string, any>();
	selectedFiltersVariables$ = new BehaviorSubject<{ [key: string]: any }>({});
	readonly selectedFilterVariablesFlattened$ = this.selectedFiltersVariables$.pipe(
		map(flattenCostOfWorkFilter)
	);

	quickSearchValue$ = new BehaviorSubject<string>(null);
	clearSearch$ = new Subject<void>();

	private initialFilters: ICmmsFilter[] = [];
	private availableFilters: ICmmsFilter[] = [];
	private selectedFilters: ICmmsSelectedFilter[] = [];

	private filterSelectionSubject = new BehaviorSubject(this.selectedFilters);
	private filterListSubject = new BehaviorSubject(this.availableFilters);

	constructor() {
		this.selectedFilters$ = this.filterSelectionSubject.asObservable();
		this.filterList$ = this.filterListSubject.asObservable();
	}

	updateSelectedFilterValue(filterColumnKey: string, filterValue: any): void {
		// if we cleared the filter value but leave the filter button
		if (this.isFilterValueEmpty(filterValue)) {
			this.removeSelectedFilterVariable(filterColumnKey);
		} else {
			this.selectedFiltersVariablesMap.set(filterColumnKey, filterValue);
		}
		this.selectedFiltersVariables$.next(
			Object.fromEntries(this.selectedFiltersVariablesMap)
		);
	}

	updateSelectedFilterValueFromObject(
		valuesObject: { [key: string]: any },
		dispatchChanges = true
	): void {
		for (const [key, value] of Object.entries(valuesObject)) {
			if (!value && this.selectedFiltersVariablesMap.has(key)) {
				this.selectedFiltersVariablesMap.delete(key);
			}
			this.selectedFiltersVariablesMap.set(key, value);
		}

		if (dispatchChanges) {
			this.selectedFiltersVariables$.next(
				Object.fromEntries(this.selectedFiltersVariablesMap)
			);
		}
	}

	addFilterToSelection(filters: ICmmsFilter[], autoFocus: boolean = true): void {
		const selectedFilters = [...this.selectedFilters];
		filters.forEach(filter => {
			const index = this.availableFilters.findIndex(
				filterItem => filterItem?.columnName === filter.columnName
			);
			/* Reserve place with 'null' to not modify array length that will allow
		 restoring filter later at the same place
		*/
			this.availableFilters[index] = null;
			selectedFilters.push({
				selectedFilter: filter,
				initialIndex: index,
				icon: this.filterTypeIcons[filter.type],
				autoFocus,
			});
		});

		this.selectedFilters = selectedFilters;

		this.dispatchFilterChanges();
	}

	addQuickFiltersToSelection(filters: ICmmsFilter[]): void {
		if (filters?.length) {
			filters.forEach(filter => {
				this.selectedFilters.push({
					selectedFilter: filter,
					initialIndex: -1,
					icon: this.filterTypeIcons[filter.type],
				});
			});
			this.dispatchFilterChanges();
		}
	}

	removeFilterFromSelection(filter: ICmmsSelectedFilter): void {
		const index = this.selectedFilters.findIndex(
			filterItem => filterItem.initialIndex === filter.initialIndex
		);
		this.selectedFilters.splice(index, 1);
		/* Replace reserved 'null' place in availableFilter with filter */
		this.availableFilters[filter.initialIndex] = filter.selectedFilter;

		if (filter.selectedFilter.type === 'date') {
			const columnName = filter.selectedFilter.columnName;
			this.removeSelectedFilterVariables([`${columnName}Before`, `${columnName}After`]);
		} else {
			this.removeSelectedFilterVariable(filter.selectedFilter.columnName);
		}

		this.dispatchFilterChanges();
	}

	removeSelectedFilterVariable(filterColumnKey: string): void {
		if (this.selectedFiltersVariablesMap.has(filterColumnKey)) {
			this.selectedFiltersVariablesMap.delete(filterColumnKey);
			this.selectedFiltersVariables$.next(
				Object.fromEntries(this.selectedFiltersVariablesMap)
			);
		}
	}

	removeSelectedFilterVariables(filterColumnKeys: string[]): void {
		for (const key of filterColumnKeys) {
			if (this.selectedFiltersVariablesMap.has(key)) {
				this.selectedFiltersVariablesMap.delete(key);
			}
		}
		this.selectedFiltersVariables$.next(
			Object.fromEntries(this.selectedFiltersVariablesMap)
		);
	}

	setAvailableFilters(filters: ICmmsFilter[], propagateChange = true): void {
		this.initialFilters = [...filters];
		this.availableFilters = [...filters];
		// This is only called once on filters init
		if (propagateChange) {
			this.filterListSubject.next(this.availableFilters);
		}
	}

	mergeAndSetFilters(
		filters: ICmmsFilter[],
		selectedFilters: ICmmsSelectedFilter[],
		selectedFiltersVariables: { [key: string]: any }
	): void {
		this.setAvailableFilters(filters, false);

		if (selectedFilters?.length) {
			selectedFilters.forEach(selectedFilter => {
				//We need to ignore those indexes in data, and set correct ones
				const filter = structuredClone(selectedFilter.selectedFilter);
				filter.hideCloseButton = false;
				const index = this.availableFilters.findIndex(
					filterItem => filterItem?.columnName === filter.columnName
				);
				/* Reserve place with 'null' to not modify array length that will allow
				 restoring filter later at the same place
				*/
				this.availableFilters[index] = null;

				const preselectedValue = this.getPreselectedValueFromFiltersVariables(
					selectedFiltersVariables,
					filter.columnName
				);

				if (preselectedValue !== undefined) {
					filter.preselectedValue = preselectedValue;
				}

				this.selectedFilters.push({
					selectedFilter: filter,
					initialIndex: index,
					icon: this.filterTypeIcons[filter.type],
					autoFocus: false,
				});
			});
		}

		this.updateSelectedFilterValueFromObject(selectedFiltersVariables, false);

		this.dispatchFilterChanges();
		this.selectedFiltersVariables$.next(
			Object.fromEntries(this.selectedFiltersVariablesMap)
		);
	}

	dispatchFilterChanges(): void {
		this.filterListSubject.next(this.filtersWithoutFalsyValues());
		this.filterSelectionSubject.next(this.selectedFilters);
	}

	clearAllFilters(): void {
		this.availableFilters = [...this.initialFilters];
		this.selectedFilters = [];
		this.clearFilterVariables();
		this.dispatchFilterChanges();
	}

	destroyFilters(): void {
		this.clearFilterVariables();
		this.availableFilters = [];
		this.selectedFilters = [];
		this.selectedFiltersVariablesMap.clear();
		this.quickSearchValue$.next(null);
		this.clearSearch$.next();
		this.dispatchFilterChanges();
	}

	applyBimFilter(filtersWithLocation: ICmmsFilter[], clearAllFilters = false): void {
		if (clearAllFilters) {
			this.clearAllFilters();
		}
		this.defaultFilterValues.set('includeFromIfc', false);
		this.setAvailableFilters(filtersWithLocation);
		this.addFilterToSelection(
			[
				{
					type: 'bim',
					columnName: 'includeFromIfc',
					name: 'BIM',
					preselectedValue: false,
				},
			],
			false
		);
	}

	private clearFilterVariables(): void {
		if (this.selectedFiltersVariablesMap.size) {
			this.selectedFiltersVariablesMap.clear();
			this.selectedFiltersVariables$.next(
				Object.fromEntries(this.selectedFiltersVariablesMap)
			);
		}
	}

	// Because FUCK JS WITH HIS typeof >_<
	private isFilterValueEmpty(value): boolean {
		const jsStringifiedObjects = ['{}', '[]'];
		if (typeof value === 'string') {
			return !value.trim().length;
		}
		return jsStringifiedObjects.includes(JSON.stringify(value));
	}

	private filtersWithoutFalsyValues(): ICmmsFilter[] {
		return this.availableFilters.filter(Boolean);
	}

	private getPreselectedValueFromFiltersVariables(
		selectedFiltersVariables: {
			[key: string]: any;
		},
		columnName: string
	) {
		const dateRanges = {
			deadline: { start: 'deadlineAfter', end: 'deadlineBefore' },
			created: { start: 'createdAfter', end: 'createdBefore' },
			updated: { start: 'updatedAfter', end: 'updatedBefore' },
		};

		if (selectedFiltersVariables[columnName] !== undefined) {
			return selectedFiltersVariables[columnName];
		}

		if (dateRanges[columnName]) {
			const { start, end } = dateRanges[columnName];
			if (selectedFiltersVariables[start] || selectedFiltersVariables[end]) {
				return {
					start: selectedFiltersVariables[start],
					end: selectedFiltersVariables[end],
				};
			}
		}
	}
}

const flattenCostOfWorkFilter = (
	filterVariables: Record<string, any>
): Record<string, any> => {
	if (filterVariables.costOfWork) {
		const { costOfWork, ...newFilterVariables } = filterVariables;
		return {
			...newFilterVariables,
			...costOfWork,
		};
	}
	return filterVariables;
};
